<script setup>
import { getI18n } from '@/plugins/i18n'
import { ref } from 'vue'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxItem } from 'devextreme-vue/form'
import { DxTabPanel } from 'devextreme-vue/tab-panel'

import * as dataGrid from '@/composables/data-grid'
import { useAddressRef } from '@/composables/data-grid-column'

import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CustomerPrivacyModuleGrid from '@/components/registry/CustomerPrivacyModuleGrid.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'

const props = defineProps({
  subject: {
    type: Object,
    required: true
  },
  contactType: Number,
  entityLock: {
    type: Object,
    required: true
  },
})
const subjectRefId = props.subject.id
const gridId = 'contact-grid'
const { gridRef } = dataGrid.useGridRef()

const i18n = getI18n()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const { currentCountryId, currentStateId } = useAddressRef()

const formInstance = ref(null)
const isDefaultDeliveryAddressCell = ref(null)

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.subjectContactsUrl,
  { filter: ['subject_ref', '=', subjectRefId] }
)

const rowButtons = props.entityLock.getEntityLockButtons()

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    fixed: false
  },
  {
    dataField: 'contact_type',
    caption: i18n.t('common.contactType'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedSubjectContactTypes(), displayExpr: 'name' }),
    validationRules: [{ type: 'required' }],
  },
  { dataField: 'is_default_delivery_address', caption: i18n.t('common.isDefaultDeliveryAddress'), dataType: 'boolean'},
  { dataField: 'name', caption: i18n.t('common.nameAlt'), validationRules: [{ type: 'required' }] },
  { dataField: 'address.country_name', caption: i18n.t('common.country') },
  {
    dataField: 'address.country',
    editCellTemplate: 'countryEditor',
    setCellValue: (newData, value) => {
      newData.address = {}
      newData.address.country = currentCountryId.value = value?.id
      newData.address.state = currentStateId.value = null
      newData.address.city = null
      newData.address.zip_code = null
      newData.address.state_text = newData.address.city_text = null
    },
    validationRules: [{ type: 'required' }],
    visible: false,
    showInColumnChooser: false,
  },
  { dataField: 'address.state_name', caption: i18n.t('common.state') },
  {
    dataField: 'address.state',
    editCellTemplate: 'stateEditor',
    setCellValue: (newData, value, currentData) => {
      newData.address = {}
      newData.address.state = newData.address.state_text = newData.address.city = newData.address.city_text = newData.address.zip_code = null

      const actualAddress = {...currentData.address, ...newData.address}

      const currentCountry = webSiteStore.countries.find(x => x.id === actualAddress?.country)
      if (currentCountry?.has_structured_location_data) {
        newData.address.state = currentStateId.value = value?.id
      } else {
        newData.address.state_text = value
      }
    },
    visible: false,
    showInColumnChooser: false,
  },
  { dataField: 'address.city_name', caption: i18n.t('common.city') },
  {
    dataField: 'address.city',
    editCellTemplate: 'cityEditor',
    setCellValue: (newData, value, currentData) => {
      newData.address = newData.address || {}
      newData.address.zip_code = newData.address.city = newData.address.city_text = null

      const actualAddress = {...currentData.address, ...newData.address}

      const currentCountry = webSiteStore.countries.find(x => x.id === actualAddress?.country)
      if (currentCountry?.has_structured_location_data) {
        newData.address.state = currentStateId.value = value?.state
        newData.address.city = value?.id
        newData.address.zip_code = value?.zip_code
      } else {
        newData.address.city_text = value
      }
    },
    validationRules: [
      {
        type: 'custom',
        message: i18n.t('common.required'),
        reevaluate: true,
        validationCallback: (e) => {
          return e.value !== null || e.data.address.city_text
        }
      }
    ],
    visible: false,
    showInColumnChooser: false
  },
  { dataField: 'address.street', caption: i18n.t('common.street'), visible: true },
  {
    dataField: 'address.zip_code',
    caption: i18n.t('common.zipCode'),
    validationRules: [{ type: 'required' }],
    visible: false,
  },
  { dataField: 'phone', caption: i18n.t('common.phone') },
  { dataField: 'phone_ext', caption: i18n.t('common.phoneExt') },
  { dataField: 'mobile', caption: i18n.t('common.mobile') },
  { dataField: 'email', caption: i18n.t('common.email') },
]

const { defaultActionTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.subjectContact')}`
  if (i.id) {
    title += ` ${i.code}`
  }
  return `${title}`
}

const onInitialized = (e) => {
  formInstance.value = e.component
}

const customizeItem = (item) => {
  if (item && item.dataField === 'is_default_delivery_address') {
    isDefaultDeliveryAddressCell.value = item
  }
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 2,
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colCount: 3,
      items: [
        { dataField: 'contact_type' },
        { dataField: 'name' },
        { dataField: 'is_default_delivery_address' },
      ]
    },
   dataGrid.useDefaultAddressEditing('address', 2, 2),
    {
      itemType: 'group',
      caption: i18n.t('common.contactInfo'),
      colSpan: 2,
      colCount: 4,
      items: [
        { dataField: 'phone', editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
        { dataField: 'phone_ext', editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
        { dataField: 'mobile', editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
        { dataField: 'email' },
      ]
    },
  ],
  onInitialized: onInitialized,
  customizeItem: customizeItem,
}, {
  allowUpdating: (e) => !e.row.data.is_portal && !props.entityLock?.isLocked(false, subjectRefId),
  allowDeleting: (e) => !e.row.data.is_portal && !props.entityLock?.isLocked(false, subjectRefId),
  onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
})

const disableFields = (itemsArray) => {
  itemsArray.forEach((arrayItem) => {
    if (arrayItem) {
      if (arrayItem.itemType === 'group') {
        if (!arrayItem.items) return
        if (currentSubjectType.value) {
          arrayItem.items.forEach((item) => {
            item.editorOptions.disabled = !currentSubjectType.value.has_identity_data
          })
        }
      }
    }
  })
}

const onInitNewRow = (e) => {
  e.data.subject_ref = subjectRefId
  e.data.is_default_delivery_address = false
  e.promise = registryStore.api.getSubjectContactNextIdAsync().then(res => { e.data.code = res.data })
}

const onEditorPreparing = (e) => {
  const defaultValueChanged = e.editorOptions.onValueChanged

  if (e.parentType === 'dataRow' && e.dataField === 'contact_type') {
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      disableFields([isDefaultDeliveryAddressCell.value])
      formInstance.value?.repaint()
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'is_default_delivery_address') {
    e.editorOptions.disabled = e.row?.data?.contact_type !== portalStore.subjectContactTypes.DELIVERY_ADDRESS
  }
  if (e.parentType === 'dataRow') {
    currentCountryId.value = e.row?.data?.address?.country
  }
}

const onRowPrepared = (e) => {
  if (e.rowType === 'data' && e.data.contact_type !== portalStore.subjectContactTypes.CONTACT) {
    e.rowElement.querySelector('.dx-command-expand')?.firstChild?.classList?.remove('dx-datagrid-group-closed')
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onInitNewRow: onInitNewRow,
  onRowPrepared: onRowPrepared,
  masterDetail: {
    enabled: props.contactType === registryStore.contactTypes.CUSTOMER,
    template: 'master-detail',
  },
  onEditorPreparing: onEditorPreparing,
  onToolbarPreparing: (e) => dataGrid.useDefaultToolbar(e, {}),
  height: '100%',
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      class="q-mb-lg"
    >
      <template #master-detail="{ data: contact }">
        <dx-tab-panel>
          <dx-item
            :title="$t('common.privacy')"
            template="privacy-tab"
          />
          <template #privacy-tab>
            <customer-privacy-module-grid
              :subject="props.subject"
              :entity-lock="props.entityLock"
              :customer-contact-id="-contact.data.id"
              :privacy-module-type="registryStore.privacyModuleTypes.CONTACT"
            />
          </template>
        </dx-tab-panel>
      </template>
      <template #countryEditor="{ data: cellInfo }">
        <country-drop-down-box
          :data="cellInfo"
          :disabled="props.entityLock.isLocked()"
        />
      </template>
      <template #cityEditor="{ data: cellInfo }">
        <city-drop-down-box
          :data="cellInfo"
          :value="cellInfo.data.address?.city"
          :value-text="cellInfo.data.address?.city_text"
          :country="currentCountryId"
          :state="currentStateId"
          :disabled="props.entityLock.isLocked()"
        />
      </template>
      <template #stateEditor="{ data: cellInfo }">
        <div>
          <state-drop-down-box
            :data="cellInfo"
            :value="cellInfo.data.address?.state"
            :value-text="cellInfo.data.address?.state_text"
            :country="currentCountryId"
            :disabled="props.entityLock.isLocked()"
          />
        </div>
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Subject'"
          :master-entity-id="subjectRefId"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :master-entity-id="subjectRefId"
          :grid="gridRef"
          :entity-lock="props.entityLock"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
<style>
</style>
